import { Flex, Box } from '@landr/maestro';
import React from 'react';
import { css } from '@landr/styled-system/css';

interface BarProps {
    color: string;
    width: number;
    delayInSeconds: number;
    durationInSeconds: number;
    scaleY: number;
    verticalAlignment: 'top' | 'center' | 'bottom';
    cornerRadius?: number;
}

const Bar = ({
    color,
    width,
    delayInSeconds,
    durationInSeconds,
    scaleY,
    verticalAlignment,
    cornerRadius = 0,
}: BarProps) => {
    const barStyle = css({
        backgroundColor: color ? color : 'surface.low',
        height: '100%',
        borderRadius: `${cornerRadius}px`,
        transformOrigin: `${verticalAlignment} center`,
    });

    return (
        <Box
            className={barStyle}
            style={
                {
                    animation: `barAnimation ${durationInSeconds}s infinite alternate linear`,
                    animationDelay: `${delayInSeconds}s`,
                    '--scale-y': scaleY,
                    width: `${width}px`,
                } as React.CSSProperties
            }
        />
    );
};

export const SoundVisualizer = (props: {
    barCount: number;
    barWidth: number;
    height: number;
    color: string;
    durationInSeconds: number;
    cornerRadius?: number;
    verticalAlignment?: 'top' | 'center' | 'bottom';
}) => {
    const items = Array.from({ length: props.barCount }, (_, i) => ({
        id: i,
        delayInSeconds: Math.random() * 1,
        durationInSeconds: props.durationInSeconds + 0.2 * Math.random(),
        scaleY: Math.random() * 0.75 + 0.55,
    }));

    return (
        <Flex
            display="flex"
            height={`${props.height}px`}
            gap="2px"
            borderRadius="4px"
        >
            {items.map((item) => (
                <Bar
                    key={`bar-${item.id}`}
                    color={props.color}
                    width={props.barWidth}
                    delayInSeconds={item.delayInSeconds}
                    durationInSeconds={item.durationInSeconds}
                    scaleY={item.scaleY}
                    verticalAlignment={props.verticalAlignment || 'bottom'}
                    cornerRadius={props.cornerRadius || 0}
                />
            ))}
        </Flex>
    );
};

// Memoize the component to prevent unnecessary re-renders
const WaveformIcon = React.memo(
    (props: {
        barHeight: number;
        barWidth: number;
        barCount: number;
        durationInSeconds: number;
        barColor: string;
        verticalAlignment: 'top' | 'center' | 'bottom';
        cornerRadius: number;
    }) => {
        const {
            barHeight,
            barWidth,
            barCount,
            durationInSeconds,
            barColor,
            verticalAlignment,
            cornerRadius,
        } = props;

        return (
            <Flex
                width="64px"
                height="64px"
                backgroundColor="surface.inverted"
                borderRadius="100%"
                alignItems="center"
                justifyContent="center"
            >
                <Flex height={`${barHeight}px`}>
                    <SoundVisualizer
                        barWidth={barWidth}
                        height={barHeight}
                        durationInSeconds={durationInSeconds}
                        barCount={barCount}
                        color={barColor}
                        verticalAlignment={verticalAlignment}
                        cornerRadius={cornerRadius}
                    />
                </Flex>
            </Flex>
        );
    },
);

export default WaveformIcon;
